/**
 * Ein klingelndes Telefon gehört auf jeden Bildschirm.
 *
 * ## Der Befund
 *
 * Der einzige Zuhörer für `call.incoming` saß **in** `routes/Anruf.tsx` — und
 * das Bauteil wird nur gerendert, wenn man gerade auf dem Reiter „Anrufe"
 * steht. Wer auf „Familie", im Chat oder in den Einstellungen war, bekam von
 * einem Anruf nichts mit: kein Klingeln, keine Kachel, nichts. Der Anrufende
 * sah „Es klingelt…", bis er aufgab.
 *
 * Von Gerät zu Gerät funktionierte deshalb praktisch nie ein Anruf. In den
 * Tests mit drei Browsern fiel es nicht auf, weil dort alle Fenster auf dem
 * Anrufbildschirm standen — die Bedingung, unter der es geht.
 *
 * Dieselbe Fehlerklasse wie schon dreimal in diesem Projekt: Der Code war
 * fertig und richtig, nur rief ihn nichts auf, wenn es darauf ankam.
 *
 * ## Was diese Datei tut
 *
 * Sie hält den gerade klingelnden Anruf an einer Stelle, die nicht davon
 * abhängt, welcher Bildschirm offen ist. `App.tsx` meldet sich einmal an und
 * zeigt die Annehmen-Kachel über allem; `routes/Anruf.tsx` holt sich einen
 * bereits angenommenen Anruf beim Aufbauen ab.
 *
 * ## Was sie NICHT tut
 *
 * Sie weckt kein Gerät mit dunklem Bildschirm. Dafür bräuchte es eine
 * Push-Nachricht für Anrufe, und die gibt es serverseitig nicht (`call.incoming`
 * geht nur über den WebSocket). Solange beide Apps offen sind, klingelt es
 * jetzt; liegt das Telefon gesperrt in der Tasche, weiterhin nicht.
 */

export interface Klingelt {
  callId: string
  von: string
  kind: string
}

type Horcher = (k: Klingelt | null) => void

let klingelt: Klingelt | null = null
const horcher = new Set<Horcher>()

/**
 * Der Anruf, der gerade begonnen oder angenommen wurde.
 *
 * Der Weg dorthin führt über drei Stellen, und alle drei brauchen ihn: die
 * Annehmen-Kachel, der Knopf „Anrufen" auf der Familienseite und
 * „Dazukommen" auf der Startseite. Jede legt den Anruf hier ab und wechselt
 * den Bereich.
 *
 * Absichtlich eine Übergabe und kein Zustand: Wer ihn abholt, bekommt ihn
 * einmal. Bliebe er stehen, würde der Anrufbildschirm beim nächsten Öffnen in
 * ein längst beendetes Gespräch springen.
 */
let laufender: unknown = null

export function klingeltJetzt(): Klingelt | null {
  return klingelt
}

export function klingelnSetzen(k: Klingelt | null): void {
  klingelt = k
  for (const h of horcher) h(k)
}

export function beimKlingeln(h: Horcher): () => void {
  horcher.add(h)
  return () => { horcher.delete(h) }
}

const anrufHorcher = new Set<(call: unknown) => void>()

export function anrufUebergeben(call: unknown): void {
  laufender = call
  /*
   * Auch melden, nicht nur ablegen.
   *
   * Das Abholen beim Aufbauen reicht nicht: Wer den Anruf annimmt, während er
   * ohnehin schon auf „Anrufe" steht, ändert mit `location.hash = '#anrufen'`
   * gar nichts — kein `hashchange`, kein neues Bauteil, kein Abholen. Der
   * angenommene Anruf bliebe liegen, und auf dem Bildschirm stünde weiter die
   * Anrufliste.
   *
   * Beide Wege sind nötig: Der Aufruf hier für den Bildschirm, der schon
   * offen ist, das Abholen für den, der erst dadurch entsteht.
   */
  for (const h of anrufHorcher) h(call)
}

/** Meldet einen Anruf, der beginnt, während der Bildschirm schon steht. */
export function beimAnruf(h: (call: unknown) => void): () => void {
  anrufHorcher.add(h)
  return () => { anrufHorcher.delete(h) }
}

/** Holt den laufenden Anruf ab — genau einmal. */
export function anrufAbholen<T>(): T | null {
  const c = laufender as T | null
  laufender = null
  return c
}
